<template>
  <div class="maskedInput">
    <v-text-field
      :key="String(label)"
      :class="{ pullRight: label.includes('Phone') }"
      :label="label"
      :prefix="prefix"
      :hint="hint"
      :value="maskedText"
      @keypress="checkAllowed"
      @input="handleInput"
      @blur="validate"
      :error-messages="errorMessages"
      ref="inputBox"
      :required="required"
      :prepend-inner-icon="icon"
      :disabled="disabled"
      filled
      rounded
      height="56"
    >
      <template v-slot:label>
        <span>{{ label }}</span>
        <span v-if="showRequiredAsterisk" class="required-asterisk">*</span>
      </template>
    </v-text-field>
  </div>
</template>

<script>
export default {
  props: {
    label: {
      type: String,
      default: "",
    },

    prefix: {
      type: String,
      default: " ",
    },

    icon: {
      type: String,
      default: "",
    },

    hint: {
      type: String,
      default: "",
    },

    mask: {
      type: String,
      default: "",
    },

    deMask: {
      type: String,
      default: "justDigit",
    },

    validation: {
      type: String,
      default: "",
    },

    errorMessages: {
      type: String,
      default: "",
    },

    limitRawLength: {
      type: [Number, String],
      default: null,
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    required: {
      type: Boolean,
      default: false,
    },

    raw: {},
  },

  data() {
    return {
      localRaw: this.raw,
      errors: this.$parent.errors || this.$parent.$parent.errors,
    };
  },

  computed: {
    maskedText() {
      if (this.mask) return this[this.mask]();
      return this.localRaw;
    },
    showRequiredAsterisk() {
      return this.required && !this.label?.includes("*");
    },
  },
  watch: {
    raw(newVal) {
      // Update localRaw when raw prop changes from outside
      if (newVal !== this.localRaw) {
        this.localRaw = newVal;
      }
    },
    maskedText() {
      if (
        this.inputField &&
        this.selectionStart &&
        this.maskedText &&
        this.selectionStart < this.maskedText.length
      )
        this.$nextTick(() => this.setFocus());
    },
  },

  methods: {
    checkAllowed(e) {
      // Don't allow the input of any characters that wouldn't survive the de-masking or it exceeds a provided limit length
      if (
        !this.removeMask(e.key) ||
        !this.limitLength(e.target.value + e.key, this.limitRawLength)
      )
        event.preventDefault();
    },

    removeMask(raw) {
      if (this.mask && this.deMask) return this[this.deMask](raw);
      return raw;
    },

    setFocus() {
      this.inputField.focus();
      this.inputField.selectionStart = this.selectionStart;
      this.inputField.selectionEnd = this.selectionStart;
    },

    handleInput($event) {
      this.localRaw = this.removeMask($event);

      try {
        // Ridiculous path to find attributes of the current input box
        this.inputField = this.$refs.inputBox.$el.children[0].children[0].children[1].children[2];

        // If the user has moved the cursor to a character that's not the last, we need to store the index to make sure it stays there after masking
        this.selectionStart =
          this.inputField.selectionStart < $event.length
            ? this.inputField.selectionStart
            : null;
      } catch (e) {
        console.error("failed to find input in mask component", e);
      }

      this.$emit("update:raw", this.localRaw);
      this.$emit("input", this.localRaw);

      if (this.errorMessages) this.validate();
    },

    limitLength(text, maxLength) {
      // Only compare the maxLength if the text and max length are set
      return !(text && maxLength) || this.removeMask(text).length <= maxLength;
    },

    validate() {
      if (this.validation) return this[this.validation]();
    },

    justDigit: (raw) => raw.replace(/\D/g, ""),

    nonempty() {
      if (!this.localRaw) {
        this.$set(
          this.errors,
          "nameError",
          this.$vuetify.lang.t("$vuetify.forms.emptyError")
        );
      } else {
        this.$delete(this.errors, "nameError");
      }
    },

    phoneNumber() {
      if (this.localRaw) {
        // Unpretty the inputs value
        const filteredNumber = this.removeMask(this.localRaw);

        // Parse each section, adding 'pretty' values only on the left of where the user has typed
        let area_code = filteredNumber.slice(0, 3) || "";
        if (area_code) {
          area_code = " (" + area_code;

          if (area_code.length == 5) area_code += ") ";
        }

        let part_1 = filteredNumber.slice(3, 6) || "";
        if (part_1 && part_1.length == 3) part_1 += "-";

        let part_2 = filteredNumber.slice(6, 10) || "";

        return `${area_code}${part_1}${part_2}`;
      }

      return null;
    },
    validatePhone() {
      // Phone numbers must be a fixed length
      if (
        !(
          this.localRaw &&
          this.removeMask(this.localRaw).length == this.limitRawLength
        )
      ) {
        this.$set(
          this.errors,
          "phoneError",
          this.$vuetify.lang.t("$vuetify.forms.billing.phoneError")
        );
      } else {
        this.$delete(this.errors, "phoneError");
      }
    },

    validateEmail() {
      const matchRegex = (email) => {
        var re = /\S+@\S+\.\S+/;
        return re.test(email);
      };

      if (!matchRegex(this.localRaw)) {
        this.$set(
          this.errors,
          "emailError",
          this.$vuetify.lang.t("$vuetify.forms.billing.emailError")
        );
      } else {
        this.$delete(this.errors, "emailError");
      }
    },
  },
};
</script>

<style scoped>
.required-asterisk {
  color: #e53935;
  margin-left: 4px;
  font-weight: 600;
}
</style>
